import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import { VisuallyHidden } from '../../base/VisuallyHidden/VisuallyHidden.js';

import {
	IRangeBounds,
	IRangeChartConfiguration,
	IRangeChartProps,
	IRangeChartState,
	IRangeTick,
	TRangeValue,
} from './models/index.js';
import { IRangeChartTranslationPlaceholders } from './models/IRangeChartTranslationPlaceholders.js';
import Axis from './styledComponents/Axis.js';
import AxisTick from './styledComponents/AxisTick.js';
import Bar from './styledComponents/Bar.js';
import InnerWrapper from './styledComponents/InnerWrapper.js';
import Point from './styledComponents/Point.js';
import PointContent from './styledComponents/PointContent.js';
import Wrapper from './styledComponents/Wrapper.js';
import { RANGE_CHART_CATEGORY_KEY, RANGE_CHART_COMPONENT_KEY } from './RangeChart.meta.js';

export class RangeChart extends ComponentBase<IRangeChartProps, IRangeChartState, IRangeChartConfiguration> {
	categoryKey = RANGE_CHART_CATEGORY_KEY;
	componentKey = RANGE_CHART_COMPONENT_KEY;
	translationContext: ITranslationContextValue | undefined;

	constructor(props: IRangeChartProps) {
		super(props);

		makeObservable(this, {
			ticks: computed,
			bounds: computed,
			barStyle: computed,
			accessibilityText: computed,
		});
	}

	get ticks(): IRangeTick[] {
		const { ticks = [] } = this.props;
		return ticks.sort((a, b) => {
			return a.value - b.value;
		});
	}

	get bounds(): IRangeBounds {
		const ticks = this.ticks;
		const allNumberValues = ticks.every((tick) => typeof tick.value === 'number');
		let bounds: IRangeBounds = {};
		if (allNumberValues) {
			bounds = this.ticks.reduce((acc: { max?: number; min?: number }, tick) => {
				const { value } = tick;
				if (typeof value === 'number') {
					if (common.commonUtils.isUndefined(acc.max)) {
						acc.max = value;
					} else {
						acc.max = Math.max(value, acc.max);
					}
					if (common.commonUtils.isUndefined(acc.min)) {
						acc.min = value;
					} else {
						acc.min = Math.min(value, acc.min);
					}
				}
				return acc;
			}, {});
			bounds.count = this.ticks.length;

			if (typeof bounds.max === 'number' && typeof bounds.min === 'number') {
				bounds.range = bounds.max - bounds.min;
			}
		}
		return bounds;
	}

	scale(value: TRangeValue): number {
		const bounds = this.bounds;
		const offsetValue = value - (bounds.min || 0);
		return (offsetValue / (bounds.range || 1)) * 100;
	}

	getPositionStyle(leftPercent: number): React.CSSProperties {
		return {
			left: `${leftPercent}%`,
		};
	}

	get barStyle(): React.CSSProperties {
		const gradientContent: string[] = ['to right'];
		const ticks = this.ticks;
		gradientContent.push(...ticks.map((tick) => `${tick.color} ${this.scale(tick.value)}%`));
		return {
			backgroundImage: `linear-gradient(${gradientContent.join(',')})`,
		};
	}

	get accessibilityText() {
		const { value } = this.props;
		const bounds = this.bounds;
		const ticks = this.ticks;
		if (!common.commonUtils.isNullOrEmpty(bounds) && !common.commonUtils.isNullOrEmpty(ticks)) {
			let descriptiveText = '';
			// See if the value matches a tick
			const valueTick = ticks.find((elem) => elem.value === value);
			const minRangeTick = ticks.find((elem) => elem.value === bounds.min);
			const maxRangeTick = ticks.find((elem) => elem.value === bounds.max);
			const placeholders: IRangeChartTranslationPlaceholders = {};
			if (minRangeTick && maxRangeTick) {
				placeholders.maxRangeTick = maxRangeTick.label;
				placeholders.minRangeTick = minRangeTick.label;
				if (valueTick) {
					placeholders.valueTick = valueTick.label;
					descriptiveText = `Value is {valueTick} on a range of {minRangeTick} to {maxRangeTick}`;
				} else {
					const maxTick = ticks.find((elem) => elem.value > value);
					const minTick = ticks.sort((a, b) => b.value - a.value).find((elem) => elem.value < value);
					if (maxTick && minTick) {
						placeholders.maxTick = maxTick.label;
						placeholders.minTick = minTick.label;
						descriptiveText = `Value is between {minTick} and {maxTick} on a range of {minRangeTick} to {maxRangeTick}`;
					}
				}
				let translatedText = '';
				if (this.translationContext) {
					translatedText = this.translationContext.translate(descriptiveText, placeholders);
				}

				return <VisuallyHidden>{translatedText}</VisuallyHidden>;
			}
		}
		return null;
	}

	render() {
		const { className, value } = this.props;
		const pointLeftPercent = this.scale(value);
		const pointStyle = this.getPositionStyle(pointLeftPercent);
		const barStyle = this.barStyle;
		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext) => {
						this.translationContext = translationContext;
						return (
							<Fragment>
								{this.accessibilityText}
								<InnerWrapper aria-hidden="true">
									<Bar style={barStyle}>
										{!common.commonUtils.isNullOrUndefined(value) && (
											<Point style={pointStyle}>
												<PointContent />
											</Point>
										)}
									</Bar>
									<Axis>
										{this.ticks.map((tick) => {
											const tickLeftPercent = this.scale(tick.value);
											const axisTickStyle = this.getPositionStyle(tickLeftPercent);
											return (
												<AxisTick
													style={axisTickStyle}
													key={`tick_${tick.value}_${tick.label}`}
												>
													{tick.label}
												</AxisTick>
											);
										})}
									</Axis>
								</InnerWrapper>
							</Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(RangeChart), RANGE_CHART_CATEGORY_KEY, RANGE_CHART_COMPONENT_KEY);
